import { setRef } from '@dni-react-hooks/set-ref'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useControlled } from '@dni-react-hooks/use-controlled'
import * as stylex from '@stylexjs/stylex'
import { cloneElement, isValidElement, useEffect, useState, type MutableRefObject, type ReactElement, type ReactNode } from 'react'
import { Paper, type PaperProps } from '../Paper/Paper'
import { Popper, type PopperProps } from '../Popper/Popper'
import { colors } from '../tokens/colors.stylex'
import { zIndex } from '../tokens/zIndex.stylex'

const arrowBoxStyles = stylex.create({
  'top': {
    transform: 'translate(-50%, 0px)',
    bottom: 0,
    left: '50%',
    marginBottom: '-0.71em',
  },
  'bottom': {
    transform: 'translate(-50%, 0px)',
    left: '50%',
    marginTop: '-0.71em',
    top: 0,
  },
  'left': {
    transform: 'translate(0px, -50%)',
    height: '1em',
    marginRight: '-0.71em',
    right: 0,
    top: '50%',
    width: '0.71em',
  },
  'right': {
    transform: 'translate(0px, -50%)',
    height: '1em',
    left: 0,
    marginLeft: '-0.71em',
    top: '50%',
    width: '0.71em',
  },
  'auto': { display: 'none' },
  'auto-start': { display: 'none' },
  'auto-end': { display: 'none' },
  'top-start': { display: 'none' },
  'top-end': { display: 'none' },
  'bottom-start': { display: 'none' },
  'bottom-end': { display: 'none' },
  'right-start': { display: 'none' },
  'right-end': { display: 'none' },
  'left-start': { display: 'none' },
  'left-end': { display: 'none' },
})

const arrowStyles = stylex.create({
  'top': {
    transformOrigin: '100% 0',
  },
  'bottom': {
    transformOrigin: '0 100%',
  },
  'left': {
    transformOrigin: '0 0',
  },
  'right': {
    transformOrigin: '100% 100%',
  },
  'auto': {},
  'auto-start': {},
  'auto-end': {},
  'top-start': {},
  'top-end': {},
  'bottom-start': {},
  'bottom-end': {},
  'right-start': {},
  'right-end': {},
  'left-start': {},
  'left-end': {},
})

const variantStyles = stylex.create({
  light: {
    backgroundColor: colors.white,
    color: colors.grey800,
  },
  dark: {
    backgroundColor: colors.grey800,
    color: colors.white,
  },
})

const baseStyles = stylex.create({
  root: {
    zIndex: zIndex.tooltip,
  },
  paper: {
    padding: 8,
    position: 'relative',
    textWrap: 'wrap',
    maxWidth: 120,
  },
  arrowBox: {
    overflow: 'hidden',
    boxSizing: 'border-box',
    position: 'absolute',
    height: '0.71em',
    width: '1em',
  },
  arrow: {
    margin: 'auto',
    display: 'block',
    transform: 'rotate(45deg)',
    height: '100%',
    width: '100%',
  },
})

export interface TooltipProps {
  /**
   * 觸發 Tooltip 的子元素（必須為 React 元素）
   */
  children: ReactElement
  /**
   * 根元素的自訂樣式
   */
  rootStyle?: stylex.StyleXStyles
  /**
   * 內容面板的自訂樣式
   */
  paperStyle?: stylex.StyleXStyles
  /**
   * 是否強制開啟（受控模式）
   */
  open?: boolean
  /**
   * 提示文字內容
   */
  title?: ReactNode
  /**
   * 是否顯示箭頭
   * @default true
   */
  arrow?: boolean
  /**
   * 外觀變體
   * @default 'light'
   */
  variant?: 'light' | 'dark'
  /**
   * 傳遞給內部 Paper 的額外屬性
   */
  PaperProps?: Omit<PaperProps, 'rootStyle'>
  /**
   * 彈出位置
   * @default 'bottom'
   */
  placement?: PopperProps['placement']
  /**
   * 偏移量 [skidding, distance]
   * @default [0, 8]
   */
  offset?: PopperProps['offset']
  /**
   * CSS 定位策略
   */
  strategy?: PopperProps['strategy']
  /**
   * 是否停用 Portal，直接在原位渲染
   * @default true
   */
  disablePortal?: boolean
  /**
   * Tooltip 開啟時的事件回呼
   */
  onOpenSendEvent?: () => void
  /**
   * Tooltip 關閉時的事件回呼
   */
  onCloseSendEvent?: () => void
}

/**
 * Tooltip 工具提示元件，當滑鼠移入子元素時顯示提示資訊。
 * 支援多方向定位、箭頭指示與深淺色外觀。
 */
export const Tooltip = (props: TooltipProps) => {
  const {
    open: openProps,
    placement = 'bottom',
    children: childrenProp,
    title,
    variant = 'light',
    arrow = true,
    offset = [0, 8],
    paperStyle,
    PaperProps,
    rootStyle,
    strategy,
    disablePortal = true,
    onOpenSendEvent,
    onCloseSendEvent,
  } = props

  const [open, setOpen] = useControlled({
    controlled: openProps,
    default: false,
  })
  const [anchorEl, setAnchorEl] = useState<PopperProps['anchorEl']>(null)

  const handleOpenSendEvent = useCallbackEvent(() => {
    onOpenSendEvent?.()
  })

  const handleCloseSendEvent = useCallbackEvent(() => {
    onCloseSendEvent?.()
  })

  useEffect(() => {
    const handleOpen = () => {
      setOpen(true)
      handleOpenSendEvent()
    }

    const handleClose = () => {
      setOpen(false)
      handleCloseSendEvent()
    }

    anchorEl?.addEventListener('mouseenter', handleOpen)
    anchorEl?.addEventListener('mouseleave', handleClose)

    return () => {
      anchorEl?.removeEventListener('mouseenter', handleOpen)
      anchorEl?.removeEventListener('mouseleave', handleClose)
    }
  }, [anchorEl, handleCloseSendEvent, handleOpenSendEvent, setOpen])

  // to prevent runtime errors, developers will need to provide a child as a React element anyway.
  const children = isValidElement(childrenProp) ? childrenProp : <span>{childrenProp}</span>
  const handleRootRef = (value: HTMLElement) => {
    setAnchorEl(value)
    setRef(children.props.rootRef as MutableRefObject<HTMLElement>, value)
  }

  return (
    <>
      {children && cloneElement(children, { rootRef: handleRootRef })}
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement={placement}
        offset={offset}
        strategy={strategy}
        disablePortal={disablePortal}
        {...stylex.props(baseStyles.root, rootStyle)}
      >
        <Paper elevation={4} rootStyle={[baseStyles.paper, variantStyles[variant], paperStyle]} {...PaperProps}>
          {title}
          {
            arrow && (
              <span {...stylex.props(baseStyles.arrowBox, arrowBoxStyles[placement])}>
                <span {...stylex.props(baseStyles.arrow, arrowStyles[placement], variantStyles[variant])} />
              </span>
            )
          }
        </Paper>
      </Popper>
    </>
  )
}
